import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, DatePicker, Image, Input, Select, Space, Table, Tag, Tooltip, message } from 'antd';
import { CopyOutlined, InboxOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import copy from 'copy-to-clipboard';

import { doGetPartnerOrders } from '../modules/partner-orders/stores/thunk';
import { selectAllPartnerOrders } from '../modules/partner-orders/stores/slice';
import './PartnerDashboard.scss';

const { RangePicker } = DatePicker;

const STORAGE_KEY = 'partner-dashboard-col-widths';

const STATUS_COLORS = {
  DRAFT: 'default',
  UNASSIGNED: 'default',
  PENDING: 'gold',
  ASSIGNED: 'blue',
  OPTIMIZED: 'cyan',
  DISPATCHED: 'geekblue',
  PICKUP_ARRIVED: 'purple',
  COLLECTED: 'purple',
  DELIVERY_ARRIVED: 'purple',
  COMPLETED: 'green',
  SKIPPED: 'orange',
  DELIVERY_FAILED: 'red',
  PICKUP_FAILED: 'red',
  ERROR: 'red',
  SERVICE_ARRIVED: 'purple',
};

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Assigned', value: 'ASSIGNED' },
  { label: 'Dispatched', value: 'DISPATCHED' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Delivery Failed', value: 'DELIVERY_FAILED' },
  { label: 'Skipped', value: 'SKIPPED' },
];

const DEFAULT_WIDTHS = {
  orderCode: 120,
  partnerOrderRef: 140,
  name: 150,
  status: 140,
  deliveryDate: 120,
  deliveryAddress: 200,
  timeWindow: 120,
  arrivalTime: 80,
  pod: 100,
  collectedItems: 90,
  driver: 120,
  fleet: 100,
  tracking: 50,
};

const loadSavedWidths = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...DEFAULT_WIDTHS, ...JSON.parse(saved) } : { ...DEFAULT_WIDTHS };
  } catch {
    return { ...DEFAULT_WIDTHS };
  }
};

// Resizable header cell component
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;
  const startX = useRef(0);
  const startWidth = useRef(0);

  if (!width || !onResize) {
    return <th {...restProps} />;
  }

  const handleMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    startX.current = e.clientX;
    startWidth.current = width;

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX.current;
      const newWidth = Math.max(startWidth.current + diff, 50);
      onResize(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <th {...restProps}>
      {restProps.children}
      <div className="column-resize-handle" onMouseDown={handleMouseDown} role="presentation" />
    </th>
  );
};

const PartnerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const orders = useSelector(selectAllPartnerOrders);
  const { isFetching, metadata } = useSelector((state) => state.partnerOrder);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [colWidths, setColWidths] = useState(loadSavedWidths);

  const handleResize = useCallback(
    (key) => (newWidth) => {
      setColWidths((prev) => {
        const updated = { ...prev, [key]: newWidth };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    },
    [],
  );

  const fetchOrders = useCallback(() => {
    const params = { page, take: 20 };
    if (search) params.q = search;
    if (statusFilter) params.status = statusFilter;
    if (dateRange && dateRange[0]) params.deliveryDateFrom = dateRange[0].toISOString();
    if (dateRange && dateRange[1]) params.deliveryDateTo = dateRange[1].toISOString();
    dispatch(doGetPartnerOrders(params));
  }, [dispatch, page, search, statusFilter, dateRange]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setPage(1);
  };

  const handleCopyTrackingUrl = (e, record) => {
    e.stopPropagation();
    if (!record.trackingToken) return;
    const url = window.location.origin + '/t/' + record.orderCode + '/' + record.trackingToken;
    copy(url);
    messageApi.open({ type: 'success', content: 'Tracking URL copied', style: { marginTop: '68px' } });
  };

  const getArrivalTime = (order) => {
    if (!order.logs) return '-';
    const arrivalLog = order.logs.find((log) => log.status === 'DELIVERY_ARRIVED' || log.status === 'SERVICE_ARRIVED');
    return arrivalLog ? moment(arrivalLog.createdAt).format('HH:mm') : '-';
  };

  const columns = useMemo(
    () => [
      {
        title: 'Order Code',
        dataIndex: 'orderCode',
        key: 'orderCode',
        width: colWidths.orderCode,
        fixed: 'left',
        onHeaderCell: () => ({ width: colWidths.orderCode, onResize: handleResize('orderCode') }),
      },
      {
        title: 'Partner Order Ref',
        dataIndex: 'partnerOrderRef',
        key: 'partnerOrderRef',
        width: colWidths.partnerOrderRef,
        render: (val) => val || '-',
        onHeaderCell: () => ({ width: colWidths.partnerOrderRef, onResize: handleResize('partnerOrderRef') }),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: colWidths.name,
        ellipsis: true,
        onHeaderCell: () => ({ width: colWidths.name, onResize: handleResize('name') }),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: colWidths.status,
        render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{(status || '').replace(/_/g, ' ')}</Tag>,
        onHeaderCell: () => ({ width: colWidths.status, onResize: handleResize('status') }),
      },
      {
        title: 'Delivery Date',
        dataIndex: 'deliveryDate',
        key: 'deliveryDate',
        width: colWidths.deliveryDate,
        render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '-'),
        onHeaderCell: () => ({ width: colWidths.deliveryDate, onResize: handleResize('deliveryDate') }),
      },
      {
        title: 'Delivery Address',
        dataIndex: 'deliveryAddress',
        key: 'deliveryAddress',
        width: colWidths.deliveryAddress,
        ellipsis: true,
        onHeaderCell: () => ({ width: colWidths.deliveryAddress, onResize: handleResize('deliveryAddress') }),
      },
      {
        title: 'Time Window',
        key: 'timeWindow',
        width: colWidths.timeWindow,
        render: (_, record) => {
          const start = record.deliveryTimeStart || '';
          const end = record.deliveryTimeEnd || '';
          return start || end ? `${start} - ${end}` : '-';
        },
        onHeaderCell: () => ({ width: colWidths.timeWindow, onResize: handleResize('timeWindow') }),
      },
      {
        title: 'Arrival',
        key: 'arrivalTime',
        width: colWidths.arrivalTime,
        render: (_, record) => getArrivalTime(record),
        onHeaderCell: () => ({ width: colWidths.arrivalTime, onResize: handleResize('arrivalTime') }),
      },
      {
        title: 'POD',
        key: 'pod',
        width: colWidths.pod,
        render: (_, record) => {
          const podFiles = record.fileOrders?.map((fo) => fo.file).filter(Boolean) || [];
          if (podFiles.length === 0) return '-';
          return (
            <div role="presentation" onClick={(e) => e.stopPropagation()}>
              <Image.PreviewGroup>
                <Space size={4}>
                  {podFiles.map((file) => (
                    <Image
                      key={file.id}
                      src={file.url}
                      alt="POD"
                      width={36}
                      height={36}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      preview={{ mask: false }}
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          );
        },
        onHeaderCell: () => ({ width: colWidths.pod, onResize: handleResize('pod') }),
      },
      {
        title: 'Collected',
        key: 'collectedItems',
        width: colWidths.collectedItems,
        align: 'center',
        render: (_, record) => {
          const count = record._count?.collectedItems || 0;
          return count > 0 ? (
            <Tag icon={<InboxOutlined />} color="orange">
              {count}
            </Tag>
          ) : (
            '0'
          );
        },
        onHeaderCell: () => ({ width: colWidths.collectedItems, onResize: handleResize('collectedItems') }),
      },
      {
        title: 'Driver',
        key: 'driver',
        width: colWidths.driver,
        render: (_, record) => record.route?.schedulerForm?.driver?.name || '-',
        onHeaderCell: () => ({ width: colWidths.driver, onResize: handleResize('driver') }),
      },
      {
        title: 'Fleet',
        key: 'fleet',
        width: colWidths.fleet,
        render: (_, record) => record.route?.schedulerForm?.fleet?.accessCode || '-',
        onHeaderCell: () => ({ width: colWidths.fleet, onResize: handleResize('fleet') }),
      },
      {
        title: '',
        key: 'tracking',
        width: colWidths.tracking,
        align: 'center',
        render: (_, record) =>
          record.trackingToken ? (
            <Tooltip title="Copy tracking URL">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={(e) => handleCopyTrackingUrl(e, record)}
              />
            </Tooltip>
          ) : null,
        onHeaderCell: () => ({ width: colWidths.tracking, onResize: handleResize('tracking') }),
      },
    ],
    [colWidths, handleResize],
  );

  const tableComponents = useMemo(
    () => ({
      header: {
        cell: ResizableTitle,
      },
    }),
    [],
  );

  return (
    <div className="PartnerDashboard">
      {contextHolder}
      <div className="dashboard-header">
        <h1>Orders</h1>
        <Button icon={<ReloadOutlined />} onClick={fetchOrders}>
          Refresh
        </Button>
      </div>

      <div className="dashboard-filters">
        <Input.Search
          placeholder="Search by order code, name, address..."
          allowClear
          onSearch={handleSearch}
          className="search-input"
          prefix={<SearchOutlined />}
        />
        <Select
          value={statusFilter}
          onChange={handleStatusChange}
          options={STATUS_OPTIONS}
          className="status-filter"
          placeholder="Status"
        />
        <RangePicker onChange={handleDateChange} className="date-filter" />
      </div>

      <Table
        dataSource={orders}
        columns={columns}
        components={tableComponents}
        rowKey="id"
        loading={isFetching}
        scroll={{ x: 1200 }}
        size="small"
        onRow={(record) => ({
          onClick: () => navigate(`/partner/orders/${record.orderCode}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          total: metadata?.totalItems || 0,
          pageSize: 20,
          showSizeChanger: false,
          showTotal: (total) => `${total} orders`,
          onChange: (p) => setPage(p),
        }}
      />
    </div>
  );
};

export default PartnerDashboard;
